import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HrnbRow, HrnbCol } from '../../../components/controls/HrnbContainer';
import HrnbCard from '../../../components/controls/HrnbCard';
import HrnbButton from '../../../components/controls/HrnbButton';
import HrnbAlert from '../../../components/controls/HrnbAlert';
import HrnbLoader from '../../../components/controls/HrnbLoader';
import HrnbSkeleton from '../../../components/controls/HrnbSkeleton';
import { BackIcon, JoinIcon, RejectIcon, ParticipantsIcon, EditIcon, PlusIcon, DeleteIcon } from '../../../icons';
import {
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  VISIBILITY_LABELS,
  FORMAT_LABELS,
  ROUND_STATUS_COLORS,
  ROUND_STATUS_LABELS,
} from '../constants';
import { isRegistrationOpen } from '../utils';

import '../../../css/pages/BattleDetailPage.css';

/**
 * Вкладка "Информация" страницы баттла
 */
const BattleInfoTab = ({
  battle,
  coverData,
  coverLoading,
  participants,
  rounds,
  currentUser,
  isUserOrganizer,
  isRegistered,
  actionLoading,
  deleteLoading,
  onRegister,
  onUnregister,
  onDelete,
  onEditBattle,
  navigateToParticipants,
  setActiveTab,
  onOpenInviteModal,
}) => {
  const navigate = useNavigate();

  if (!battle) {
    return (
      <HrnbAlert variant="danger">
        Баттл не найден
        <HrnbButton onClick={() => navigate('/battles')} className="mt-3">
          <BackIcon style={{ width: 16, height: 16, marginRight: 8 }} />
          Вернуться к списку
        </HrnbButton>
      </HrnbAlert>
    );
  }

  const {
    title,
    description,
    type,
    status,
    format,
    visibility,
    startTime,
    endTime,
    registrationDeadline,
    prizePool,
    maxParticipants,
    organizers = [],
    createdAt,
    tags = [],
    coverImageId,
  } = battle;

  const coverUrl = coverData?.coverUrl || null;
  const statusColor = STATUS_COLORS[status] || '#6c757d';
  const typeLabel = TYPE_LABELS[type] || type || 'Не указано';
  const statusLabel = STATUS_LABELS[status] || status || 'Не указано';
  const visibilityLabel =
    VISIBILITY_LABELS[visibility] || visibility || 'Не указано';
  const formatLabel = FORMAT_LABELS[format] || format || 'Не указан';

  const participantsCount = participants.filter(
    (p) => p.role === 'PARTICIPANT'
  ).length;
  const currentRound = getCurrentRound(rounds);
  const isCurrentRoundRunning = currentRound?.status === 'RUNNING';
  const isBattleOver = status === 'FINISHED' || status === 'CANCELLED';

  // Текущий этап баттла для краткой сводки
  const currentStage = (() => {
    if (isBattleOver) return statusLabel;
    if (currentRound?.status === 'RUNNING') {
      return `Раунд ${currentRound.number}: ${currentRound.title || 'без названия'}`;
    }
    if (isRegistrationOpen(status)) return 'Регистрация участников';
    if (currentRound?.status === 'PREPARATION') {
      return `Подготовка раунда ${currentRound.number}`;
    }
    return 'Ожидание старта';
  })();

  return (
    <>
      {coverLoading && (
        <div className="battle-detail-cover battle-detail-cover--loading">
          <HrnbLoader size="lg" />
        </div>
      )}

      {coverUrl && !coverLoading && (
        <div className="battle-detail-cover">
          <img src={coverUrl} alt={title} className="battle-detail-cover-image" />
        </div>
      )}

      {!coverLoading && !coverUrl && coverImageId && (
        <div className="battle-detail-cover battle-detail-cover--error">
          <p className="text-muted">Не удалось загрузить обложку</p>
        </div>
      )}

      <HrnbRow>
        <HrnbCol xs={12} lg={8}>
          <HrnbCard title="О баттле" className="battle-detail-card">
            <div className="battle-detail-section">
              <h3 className="battle-detail-section-title">Краткая сводка</h3>
              <div className="battle-detail-summary">
                <div className="battle-detail-summary-item">
                  <span className="battle-detail-summary-label">Статус</span>
                  <span
                    className="battle-detail-status-badge"
                    style={{ backgroundColor: statusColor }}
                  >
                    {statusLabel}
                  </span>
                </div>
                <div className="battle-detail-summary-item">
                  <span className="battle-detail-summary-label">Текущий этап</span>
                  <span className="battle-detail-summary-value">{currentStage}</span>
                </div>
                <div className="battle-detail-summary-item">
                  <span className="battle-detail-summary-label">Участники</span>
                  <span className="battle-detail-summary-value">
                    {participantsCount}
                    {maxParticipants ? ` из ${maxParticipants}` : ''}
                  </span>
                </div>
                <div className="battle-detail-summary-item">
                  <span className="battle-detail-summary-label">
                    {isBattleOver ? 'Завершён' : 'Сроки'}
                  </span>
                  <span className="battle-detail-summary-value">
                    {formatDate(startTime)} — {formatDate(endTime)}
                  </span>
                </div>
              </div>
            </div>

            <div className="battle-detail-section">
              {description ? (
                <div className="battle-detail-description">{description}</div>
              ) : (
                <p className="text-muted">Описание отсутствует</p>
              )}

              {tags.length > 0 && (
                <div className="battle-detail-tags">
                  {tags.map((tag, idx) => (
                    <span key={idx} className="battle-detail-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="battle-detail-section">
              <h3 className="battle-detail-section-title">Детали</h3>
              <div className="battle-detail-grid">
                <InfoItem label="Тип:" value={typeLabel} />
                <InfoItem label="Формат:" value={
                  <span className={`battle-detail-format-badge battle-detail-format-badge--${(format || '').toLowerCase()}`}>
                    {formatLabel}
                  </span>
                } />
                <InfoItem label="Видимость:" value={visibilityLabel} />
                <InfoItem label="Начало:" value={formatDate(startTime)} />
                <InfoItem label="Окончание:" value={formatDate(endTime)} />
                {prizePool && (
                  <InfoItem label="Призовой фонд:" value={prizePool} />
                )}
                {maxParticipants && (
                  <InfoItem
                    label="Макс. участников:"
                    value={maxParticipants}
                  />
                )}
                <InfoItem
                  label="Участников:"
                  value={participants.length || 0}
                />
                {registrationDeadline && (
                  <InfoItem
                    label="Дедлайн регистрации:"
                    value={formatDate(registrationDeadline)}
                  />
                )}
                {createdAt && (
                  <InfoItem label="Создан:" value={formatDate(createdAt)} />
                )}
              </div>
            </div>

            {organizers.length > 0 && (
              <div className="battle-detail-section">
                <h3 className="battle-detail-section-title">Организаторы</h3>
                <ul className="battle-detail-list">
                  {organizers.map((o, idx) => (
                    <li key={idx} className="battle-detail-list-item">
                      {o.name || o.username || o}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="battle-detail-actions">
              {currentUser &&
                !isUserOrganizer &&
                isRegistrationOpen(status) &&
                (isRegistered ? (
                  <HrnbButton
                    variant="danger"
                    onClick={onUnregister}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      <>
                        <RejectIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        Отменить регистрацию
                      </>
                    )}
                  </HrnbButton>
                ) : (
                  <HrnbButton
                    variant="primary"
                    onClick={onRegister}
                    className="battle-detail-join-btn"
                  >
                    <JoinIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Зарегистрироваться
                  </HrnbButton>
                ))}

              <HrnbButton
                variant="outline-primary"
                onClick={navigateToParticipants}
              >
                <ParticipantsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                Участники ({participants.length})
              </HrnbButton>

              {currentUser && isUserOrganizer && (
                <>
                  <HrnbButton
                    variant="outline-primary"
                    onClick={onEditBattle}
                    className="battle-detail-edit-btn"
                  >
                    <EditIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Редактировать баттл
                  </HrnbButton>
                  <HrnbButton
                    variant="outline"
                    onClick={onOpenInviteModal}
                    className="battle-detail-invite-btn"
                  >
                    <PlusIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                    Пригласить
                  </HrnbButton>
                  <HrnbButton
                    variant="outline"
                    onClick={onDelete}
                    disabled={deleteLoading}
                    className="battle-detail-delete-btn"
                  >
                    {deleteLoading ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      <>
                        <DeleteIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        Удалить баттл
                      </>
                    )}
                  </HrnbButton>
                </>
              )}
            </div>
          </HrnbCard>
        </HrnbCol>

        <HrnbCol xs={12} lg={4}>
          {currentRound && !isCurrentRoundRunning && !isUserOrganizer ? (
            <HrnbCard
              title="Ближайший раунд"
              className="battle-detail-card battle-detail-card--dark"
            >
              <div className="battle-upcoming-round">
                <span className="battle-upcoming-round__badge">Ожидается</span>
                <div className="battle-upcoming-round__title">
                  Раунд {currentRound.number}
                  {currentRound.title ? `: ${currentRound.title}` : ''}
                </div>
                {currentRound.startTime && (
                  <div className="battle-upcoming-round__time">
                    Старт {formatDate(currentRound.startTime)}
                  </div>
                )}
              </div>
            </HrnbCard>
          ) : currentRound && (
            <HrnbCard
              title={isCurrentRoundRunning ? 'Текущий раунд' : 'Ближайший раунд'}
              className="battle-detail-card battle-detail-card--dark"
            >
              <div className="battle-current-round">
                <div className="battle-current-round-header">
                  <span className="battle-current-round-number">
                    Раунд {currentRound.number}
                  </span>
                  <span
                    className="battle-current-round-status"
                    style={{
                      backgroundColor:
                        ROUND_STATUS_COLORS[currentRound.status] || '#6c757d',
                    }}
                  >
                    {ROUND_STATUS_LABELS[currentRound.status] ||
                      currentRound.status}
                  </span>
                </div>
                <div className="battle-current-round-title">
                  {currentRound.title || 'Без названия'}
                </div>
                <div className="battle-current-round-time">
                  {formatDate(currentRound.startTime)} —{' '}
                  {formatDate(currentRound.endTime)}
                </div>
                {currentRound.theme && (
                  <div className="battle-current-round-theme">
                    🎯 {currentRound.theme}
                  </div>
                )}
                {currentRound.task && (
                  <div className="battle-current-round-task">
                    📋 {currentRound.task}
                  </div>
                )}
                <HrnbButton
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('rounds')}
                >
                  Перейти к раундам
                </HrnbButton>
              </div>
            </HrnbCard>
          )}
        </HrnbCol>
      </HrnbRow>
    </>
  );
};

/** Вспомогательный компонент */
const InfoItem = ({ label, value }) => (
  <div className="battle-detail-info-item">
    <span className="battle-detail-info-label">{label}</span>
    <span className="battle-detail-info-value">{value}</span>
  </div>
);

/**
 * Определить текущий (актуальный) раунд:
 * 1. Раунд со статусом RUNNING
 * 2. Ближайший предстоящий (PREPARATION) по времени старта
 * 3. Последний завершённый (по номеру)
 */
const getCurrentRound = (rounds = []) => {
  if (!rounds.length) return null;

  const running = rounds.find((r) => r.status === 'RUNNING');
  if (running) return running;

  const now = new Date();
  const upcoming = rounds
    .filter(
      (r) =>
        r.status === 'PREPARATION' &&
        (!r.startTime || new Date(r.startTime) >= now)
    )
    .sort((a, b) => new Date(a.startTime || 0) - new Date(b.startTime || 0));
  if (upcoming.length) return upcoming[0];

  const prep = rounds.find((r) => r.status === 'PREPARATION');
  if (prep) return prep;

  return [...rounds].sort((a, b) => (b.number || 0) - (a.number || 0))[0];
};

/** Форматирование даты */
const formatDate = (dateStr) => {
  if (!dateStr) return 'Не указано';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default BattleInfoTab;
